import React, { useEffect, useMemo, useState } from "react";
import { CLASSES, OTHER_CATEGORIES } from "./data";
import NewsEditor from "./NewsEditor2";
const API_BASE = process.env.NEXT_PUBLIC_BASE_URL;

function Sidebar({
  searchInput,
  setSearchInput,
  selectedClass,
  setSelectedClass,
  otherCategory,
  setOtherCategory,
}) {
  return (
    <aside className="w-full md:w-72 p-4 bg-gray-50 rounded-md">
      <h2 className="text-lg font-semibold mb-3">Filters</h2>

      <label className="block text-sm font-medium mb-1">Search</label>
      <div className="relative mb-4">
        <input
          type="search"
          role="searchbox"
          aria-label="Search events"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search events..."
          className="w-full pl-3 pr-10 py-2 border rounded-md text-sm shadow-sm focus:outline-none"
        />
      </div>

      <label className="block text-sm font-medium mb-1">Class</label>
      <div className="mb-4">
        <select
          aria-label="Class"
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
          className="w-full appearance-none pr-8 pl-3 py-2 border rounded-md bg-white text-sm shadow-sm hover:shadow focus:outline-none"
        >
          <option value="All">All</option>
          {CLASSES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <label className="block text-sm font-medium mb-1">Category</label>
      <div className="mb-4">
        <select
          aria-label="Category"
          value={otherCategory}
          onChange={(e) => setOtherCategory(e.target.value)}
          className="w-full appearance-none pr-8 pl-3 py-2 border rounded-md bg-white text-sm shadow-sm hover:shadow focus:outline-none"
        >
          <option value="All">All</option>
          {OTHER_CATEGORIES.map((c, i) => (
            <option key={`${c}-${i}`} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
    </aside>
  );
}

function ContentList({ items = [], onEdit, onDelete }) {
  if (!items.length) {
    return <div className="text-gray-500 col-span-full">No events found.</div>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((item) => (
        <article
          key={item.id}
          className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition h-full flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{item.title}</h3>
              <time className="text-sm text-gray-500">{item.date}</time>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="mt-2 text-sm text-gray-600 font-semibold">
                Class: <span className="font-medium">{item.class}</span>
              </p>
              <p className="mt-2 text-sm text-gray-600 font-semibold">
                Category: <span className="font-medium">{item.category}</span>
              </p>
            </div>
            {item.content ? (
              <p
                className="mt-3 text-sm text-gray-700 text-justify"
                style={{
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {item.content}
              </p>
            ) : null}
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => onEdit(item)}
              className="px-3 py-1 border rounded text-sm bg-yellow-50 border-yellow-300 hover:bg-yellow-100"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(item)}
              className="px-3 py-1 border rounded text-sm bg-red-50 border-red-300 hover:bg-red-100"
            >
              Delete
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

export default function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [selectedClass, setSelectedClass] = useState("All");
  const [otherCategory, setOtherCategory] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 6;
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => setPage(1), [selectedClass, otherCategory, query]);

  useEffect(() => {
    // fetch all news from backend and normalize fields
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/news`);
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const json = await res.json();
        const data = Array.isArray(json?.data) ? json.data : [];
        const mapped = data.map((it) => ({
          ...it,
          id: it._id ?? it.id,
          content: it.content ?? it.description ?? "",
          date:
            it.date ??
            (it.createdAt ? new Date(it.createdAt).toLocaleDateString() : ""),
          images: it.images ?? it.photos ?? [],
        }));
        setItems(mapped);
      } catch (err) {
        console.error("Failed to fetch news list:", err);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = (query || "").toLowerCase();
    return items.filter((ev) => {
      const classMatch = selectedClass === "All" || ev.class === selectedClass;
      const categoryMatch =
        otherCategory === "All" || ev.category === otherCategory;
      const queryMatch =
        !q ||
        ev.title.toLowerCase().includes(q) ||
        (ev.content || "").toLowerCase().includes(q);
      return classMatch && categoryMatch && queryMatch;
    });
  }, [items, selectedClass, otherCategory, query]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const paginated = filtered.slice(start, end);

  function handleDelete(item) {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/news/${item.id}`, {
          method: "DELETE",
        });
        if (!res.ok) throw new Error(`Status ${res.status}`);
        setItems((s) => s.filter((it) => it.id !== item.id));
      } catch (err) {
        console.error("Failed to delete:", err);
        alert("Failed to delete item. See console.");
      }
    })();
  }

  function handleEdit(item) {
    setEditingItem(item);
    setEditorOpen(true);
  }

  function handleCreate() {
    setEditingItem(null);
    setEditorOpen(true);
  }

  function handleSave(payload) {
    // payload includes id
    setItems((s) => {
      const exists = s.some((it) => it.id === payload.id);
      if (exists) return s.map((it) => (it.id === payload.id ? payload : it));
      return [payload, ...s];
    });
    setEditorOpen(false);
    setEditingItem(null);
  }

  async function handleSaveForm(form, itemParam) {
    try {
      const url = itemParam
        ? `${API_BASE}/news/${itemParam.id}`
        : `${API_BASE}/news`;
      const method = itemParam ? "PUT" : "POST";

      const res = await fetch(url, { method, body: form });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error("Save failed:", res.status, text);
        alert("Failed to save news. See console.");
        return;
      }

      const json = await res.json();
      const saved = json?.data ?? null;
      if (!saved) {
        alert("Save succeeded but no data returned.");
        return;
      }

      const payload = {
        id: saved._id ?? saved.id,
        title: saved.title,
        content: saved.content ?? saved.description,
        class: saved.class,
        category: saved.category?.name ?? saved.category,
        date:
          saved.date ??
          (saved.createdAt
            ? new Date(saved.createdAt).toLocaleDateString()
            : ""),
        images: saved.photos ?? saved.images ?? [],
      };

      handleSave(payload);
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Failed to save item. See console.");
    }
  }

  return (
    <main className="min-h-screen flex-1 p-6 flex-row lg:pl-80">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e81b0]">
            News & Events — Admin
          </h1>
          <p className="text-sm text-[#8ccfee] mt-1">
            Manage announcements and events
          </p>
        </div>
        <div>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-[#1e81b0] text-white rounded shadow hover:opacity-95"
          >
            + Create Event
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <section className="flex-1 relative">
          {/* Top filters bar (moved from sidebar) */}
          <div className="mb-4 bg-white p-3 rounded-md shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex-1 flex items-center gap-3">
                <label className="sr-only">Search</label>
                <input
                  type="search"
                  role="searchbox"
                  aria-label="Search events"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search events..."
                  className="w-full md:w-64 pl-3 pr-3 py-2 border rounded-md text-sm shadow-sm focus:outline-none"
                />

                <div className="hidden md:block">|</div>

                <select
                  aria-label="Class"
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="appearance-none pr-8 pl-3 py-2 border rounded-md bg-white text-sm shadow-sm hover:shadow focus:outline-none"
                >
                  <option value="All">All Classes</option>
                  {CLASSES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                <select
                  aria-label="Category"
                  value={otherCategory}
                  onChange={(e) => setOtherCategory(e.target.value)}
                  className="appearance-none pr-8 pl-3 py-2 border rounded-md bg-white text-sm shadow-sm hover:shadow focus:outline-none"
                >
                  <option value="All">All Categories</option>
                  {OTHER_CATEGORIES.map((c, i) => (
                    <option key={`${c}-${i}`} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <ContentList
            items={paginated}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          <NewsEditor
            item={editingItem}
            visible={editorOpen}
            onClose={() => {
              setEditorOpen(false);
              setEditingItem(null);
            }}
            onSave={async (form, itemArg) => {
              await handleSaveForm(form, itemArg);
              // close editor after save (handleSave already updates state)
              setEditorOpen(false);
              setEditingItem(null);
            }}
          />

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-[#1e81b0]">
              Showing {Math.min(start + 1, total)}-{Math.min(end, total)} of{" "}
              {total}
            </div>

            <nav className="flex items-center gap-2" aria-label="Pagination">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50 border-[#1e81b0] text-[#1e81b0] hover:bg-[#1e81b0] hover:text-white transition"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Prev
              </button>

              <div className="hidden sm:flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      aria-current={p === page ? "page" : undefined}
                      className={`px-3 py-1 border rounded ${p === page ? "bg-[#1e81b0] text-white" : "border-[#1e81b0] text-[#1e81b0] hover:bg-[#8ccfee] hover:text-white"}`}
                    >
                      {p}
                    </button>
                  ),
                )}
              </div>

              <button
                className="px-3 py-1 border rounded disabled:opacity-50 border-[#1e81b0] text-[#1e81b0] hover:bg-[#1e81b0] hover:text-white transition"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </nav>
          </div>
        </section>
      </div>
    </main>
  );
}
